import { findFontEntry } from './font-registry';
import { primaryFontFamily } from './font-options';
import { normalizeFontWeight } from './font-weights';

/** Объект схемы или живой объект Fabric — у группы дети в `objects` (JSON) или `getObjects()`. */
interface FontCarrier {
  fontFamily?: unknown;
  fontWeight?: unknown;
  objects?: unknown;
  getObjects?: () => unknown[];
  /** A media-manager placeholder keeps the font of its future text here; its own children are an Arial cue. */
  data?: { fontFamily?: unknown; fontWeight?: unknown } | null;
}

function addSpec(specs: Set<string>, fontFamily: unknown, fontWeight: unknown): void {
  if (typeof fontFamily !== 'string') return;
  const family = primaryFontFamily(fontFamily);
  if (family) specs.add(fontSpec(family, fontWeight as string | number | undefined));
}

/** Сколько ждать один шрифт. Табло в OBS без сети не должно висеть: дальше рисуется запасным. */
export const FONT_LOAD_TIMEOUT_MS = 3000;

/**
 * Text for `document.fonts.load(spec, text)`. Without it the browser takes a space and fetches only the face whose
 * `unicode-range` covers it — the Latin subset of a Google font; the Cyrillic one comes after the canvas has already
 * drawn the first Russian text with the fallback. Not a UI string: Latin, Cyrillic letters and digits.
 */
export const FONT_LOAD_SAMPLE_TEXT = 'ГОЛ Gol 0123';

/** CSS-шорткат `font` для `document.fonts.load`: `700 16px "Oswald"`. Кегль на загрузку не влияет. */
export function fontSpec(family: string, weight: string | number | undefined): string {
  return `${normalizeFontWeight(weight, family)} 16px "${family}"`;
}

function collectSpecs(items: readonly unknown[], specs: Set<string>): void {
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const node = item as FontCarrier;
    addSpec(specs, node.fontFamily, node.fontWeight);
    if (node.data && typeof node.data === 'object') addSpec(specs, node.data.fontFamily, node.data.fontWeight);
    const children = typeof node.getObjects === 'function' ? node.getObjects() : node.objects;
    if (Array.isArray(children)) collectSpecs(children, specs);
  }
}

/** Пары «вес × семейство» всех текстов схемы, включая вложенные группы, без повторов. */
export function collectFontSpecs(items: readonly unknown[]): string[] {
  const specs = new Set<string>();
  collectSpecs(items, specs);
  return Array.from(specs);
}

const familyOfSpec = (spec: string) => spec.match(/"(.+)"$/)?.[1] ?? '';

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('font load timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Ждёт загрузки шрифтов, нужных схеме (или списку спеков `700 16px "Oswald"`), и возвращает спеки,
 * которые не загрузились. Канва сама загрузку шрифта не запускает и молча рисует запасным
 * (https://developer.mozilla.org/docs/Web/API/CSS_Font_Loading_API), поэтому перед отрисовкой
 * её просят явно.
 *
 * - Отказ одного шрифта не роняет ожидание остальных.
 * - Системный шрифт и шрифт вне реестра «загружать нечего» — `load` отдаёт пустой список, это не отказ.
 * - Без `document.fonts` (jsdom) — сразу резолвится пустым списком.
 */
export async function ensureFonts(input: readonly unknown[], timeoutMs = FONT_LOAD_TIMEOUT_MS): Promise<string[]> {
  const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
  if (!fonts || typeof fonts.load !== 'function') return [];

  const specs = input.every((item) => typeof item === 'string') ? Array.from(new Set(input as string[])) : collectFontSpecs(input);

  const results = await Promise.all(
    specs.map(async (spec) => {
      try {
        const faces = await withTimeout(fonts.load(spec, FONT_LOAD_SAMPLE_TEXT), timeoutMs);
        const entry = findFontEntry(familyOfSpec(spec));
        const expectsFace = entry !== undefined && entry.source !== 'system';
        return expectsFace && faces.length === 0 ? spec : null;
      } catch {
        return spec;
      }
    }),
  );
  return results.filter((spec): spec is string => spec !== null);
}
